// ============================================================
// FILE LOCATION: src/components/TimelineBuilder/SuggestionPanel.tsx
// ============================================================

/**
 * SuggestionPanel — Interactive Assist Module
 *
 * Upgrade from informational banner to actionable guidance:
 *   - Inline "Add" CTA per suggestion (no expand required)
 *   - "Add top suggestions" batch action
 *   - Top 3 visible by default, expand for rest
 *   - Dismissible per suggestion
 *   - Assistive tone, not alert-style
 */

import React, { useState, useEffect, useCallback } from "react";
import { AppealSuggestion, EventTemplate } from "./suggestionEngine";

interface SuggestionPanelProps {
  suggestions: AppealSuggestion[];
  onAddEvent: (template: EventTemplate) => void;
  defaultCollapsed?: boolean;
}

// ── Category icons ──────────────────────────────────────────

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  conservative_therapy: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
    </svg>
  ),
  chronology: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  functional_impact: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  objective_evidence: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  denial_response: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
};

// ── Priority styling ────────────────────────────────────────

const PRIORITY_STYLES: Record<string, { label: string; className: string }> = {
  high: {
    label: "High impact",
    className: "bg-amber-50 text-amber-700 border border-amber-200/60",
  },
  medium: {
    label: "Helpful",
    className: "bg-slate-50 text-slate-600 border border-slate-200/60",
  },
  low: {
    label: "Optional",
    className: "bg-slate-50/50 text-slate-500 border border-slate-200/40",
  },
};

// ── Single suggestion row ───────────────────────────────────

const SuggestionRow: React.FC<{
  suggestion: AppealSuggestion;
  isExpanded: boolean;
  onToggle: () => void;
  onAddEvent: (template: EventTemplate) => void;
  onDismiss: () => void;
}> = ({ suggestion, isExpanded, onToggle, onAddEvent, onDismiss }) => {
  const icon = CATEGORY_ICONS[suggestion.category] || CATEGORY_ICONS.chronology;
  const priority = PRIORITY_STYLES[suggestion.priorityLabel];
  const [justAdded, setJustAdded] = useState(false);

  const handleAdd = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onAddEvent(suggestion.action.eventTemplate);
      setJustAdded(true);
      setTimeout(() => setJustAdded(false), 1500);
    },
    [suggestion, onAddEvent]
  );

  return (
    <div className="border-b border-slate-100/80 last:border-b-0 group/suggestion">
      {/* ── Row header (always visible) ────────────────── */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        className="
          w-full flex items-center gap-2.5 px-3 py-2 text-left
          hover:bg-slate-50/80
          focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset
          transition-colors cursor-pointer
        "
      >
        {/* Icon */}
        <span className="text-slate-400 flex-shrink-0">{icon}</span>

        {/* Title + preview */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-medium text-slate-800 truncate">
              {suggestion.title}
            </span>
            {priority && suggestion.priorityLabel === "high" && (
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md flex-shrink-0 ${priority.className}`}>
                {priority.label}
              </span>
            )}
          </div>
          {!isExpanded && suggestion.previewText && (
            <span className="text-xs text-slate-500 truncate block mt-0.5">
              {suggestion.previewText}
            </span>
          )}
        </div>

        {/* Inline "Add" CTA — always accessible without expanding */}
        <button
          onClick={handleAdd}
          disabled={justAdded}
          className={`
            flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium
            transition-all duration-200
            ${justAdded
              ? "bg-emerald-50 text-emerald-600 border border-emerald-200"
              : "text-blue-600 hover:bg-blue-50 border border-transparent hover:border-blue-200"
            }
          `}
          aria-label={`Add ${suggestion.title} to timeline`}
        >
          {justAdded ? (
            <>
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Added
            </>
          ) : (
            <>
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add
            </>
          )}
        </button>

        {/* Dismiss button (subtle, hover-revealed) */}
        <button
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          className="flex-shrink-0 p-0.5 text-slate-300 hover:text-slate-500 opacity-0 group-hover/suggestion:opacity-100 transition-all duration-150 rounded"
          aria-label={`Dismiss suggestion: ${suggestion.title}`}
          title="Dismiss"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Chevron */}
        <svg
          className={`w-3.5 h-3.5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* ── Expanded detail (why it matters + what to add) ─ */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-1 ml-[30px] border-l-2 border-slate-100">
          <p className="text-xs text-slate-600 mb-2 leading-relaxed">
            {suggestion.whyItMatters}
          </p>

          <div className="mb-2">
            <span className="text-xs font-medium text-slate-500">Add:</span>
            <ul className="mt-1 space-y-0.5">
              {suggestion.whatToAdd.map((item, i) => (
                <li key={i} className="text-xs text-slate-600 flex items-start gap-1.5">
                  <span className="text-slate-400 mt-0.5">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {suggestion.example && (
            <p className="text-xs text-slate-500 italic mb-2.5">
              {suggestion.example}
            </p>
          )}

          <button
            onClick={handleAdd}
            disabled={justAdded}
            className={`
              inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg
              transition-all duration-200
              ${justAdded
                ? "bg-emerald-50 text-emerald-600 border border-emerald-200"
                : "text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100"
              }
            `}
          >
            {justAdded ? (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Added to timeline
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {suggestion.action.label}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════

const SuggestionPanel: React.FC<SuggestionPanelProps> = ({
  suggestions,
  onAddEvent,
  defaultCollapsed = true,
}) => {
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(defaultCollapsed);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  // Persist panel state in session
  useEffect(() => {
    const saved = sessionStorage.getItem("timeline_suggestions_collapsed");
    if (saved !== null) setIsPanelCollapsed(saved === "true");
  }, []);

  // Auto-expand top suggestion on first panel open
  useEffect(() => {
    if (!isPanelCollapsed && suggestions.length > 0) {
      const hasAutoExpanded = sessionStorage.getItem("timeline_suggestions_auto_expanded");
      if (!hasAutoExpanded) {
        setExpandedId(suggestions[0].id);
        sessionStorage.setItem("timeline_suggestions_auto_expanded", "true");
      }
    }
  }, [isPanelCollapsed, suggestions]);

  const handlePanelToggle = () => {
    const newState = !isPanelCollapsed;
    setIsPanelCollapsed(newState);
    sessionStorage.setItem("timeline_suggestions_collapsed", String(newState));
  };

  const handleRowToggle = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const handleDismiss = useCallback((id: string) => {
    setDismissedIds((prev) => new Set([...prev, id]));
  }, []);

  if (suggestions.length === 0) return null;

  // Filter out dismissed suggestions
  const activeSuggestions = suggestions.filter((s) => !dismissedIds.has(s.id));
  if (activeSuggestions.length === 0) return null;

  // Priority-based progressive disclosure:
  // First expand → only high-impact suggestions visible
  // "Show more" → reveals everything else
  const highPrioritySuggestions = activeSuggestions.filter(
    (s) => s.priorityLabel === "high"
  );
  // If no high-priority suggestions, show the top one so it's never empty
  const initialSuggestions =
    highPrioritySuggestions.length > 0
      ? highPrioritySuggestions
      : activeSuggestions.slice(0, 1);

  const visibleSuggestions = showAll ? activeSuggestions : initialSuggestions;
  const hiddenCount = activeSuggestions.length - initialSuggestions.length;
  const hasMore = hiddenCount > 0;

  const highPriorityCount = highPrioritySuggestions.length;

  return (
    <div className="border border-slate-200/80 rounded-xl bg-white overflow-hidden">
      {/* ── Panel header ───────────────────────────────── */}
      <button
        onClick={handlePanelToggle}
        className="w-full flex items-center justify-between px-3.5 py-2.5 hover:bg-slate-50/80 transition-colors"
      >
        <div className="flex items-center gap-2">
          {/* Sparkle icon (assistive feel) */}
          <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
          <span className="text-sm font-medium text-slate-800">
            Ways to strengthen your appeal
          </span>
          {highPriorityCount > 0 && (
            <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
          )}
        </div>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isPanelCollapsed ? "" : "rotate-180"}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* ── Expanded panel ─────────────────────────────── */}
      {!isPanelCollapsed && (
        <div className="border-t border-slate-100">
          {/* Suggestion rows */}
          {visibleSuggestions.map((suggestion) => (
            <SuggestionRow
              key={suggestion.id}
              suggestion={suggestion}
              isExpanded={expandedId === suggestion.id}
              onToggle={() => handleRowToggle(suggestion.id)}
              onAddEvent={onAddEvent}
              onDismiss={() => handleDismiss(suggestion.id)}
            />
          ))}

          {/* Show more / less */}
          {hasMore && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="w-full px-3 py-2 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors flex items-center justify-center gap-1"
            >
              {showAll ? (
                <>
                  Show less
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </>
              ) : (
                <>
                  Show {hiddenCount} more suggestion{hiddenCount !== 1 ? "s" : ""}
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              )}
            </button>
          )}

          {/* Dismissed count (restore option) */}
          {dismissedIds.size > 0 && (
            <div className="px-3.5 py-1.5 border-t border-slate-100/80 flex items-center justify-center">
              <button
                onClick={() => setDismissedIds(new Set())}
                className="text-[11px] text-slate-400 hover:text-slate-600 transition-colors"
              >
                {dismissedIds.size} hidden · Restore all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SuggestionPanel;